import { useState, useEffect, useCallback, ReactNode } from 'react';

import { Box, IconButton, SxProps, Theme } from '@mui/material';

import { AppIcon } from '../designSystem/icons/AppIcon';

export type CarouselItem = Readonly<{
  imageUrl: string;
  name: string;
  description: string;
}>;

type CarouselProps = Readonly<{
  items: CarouselItem[];
  renderItem: (item: CarouselItem, isActive: boolean) => ReactNode;
  autoTransitionInterval?: number;
}>;

export const Carousel = ({ items, renderItem, autoTransitionInterval = 5000 }: CarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoTransitioning, setIsAutoTransitioning] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToIndex = useCallback(
    (newIndex: number) => {
      if (isTransitioning) return;

      setIsTransitioning(true);
      const img = new Image();

      img.src = items[newIndex].imageUrl;

      img.onload = () => {
        setTimeout(() => {
          setCurrentIndex(newIndex);
          setIsTransitioning(false);
        }, 300);
      };
    },
    [items, isTransitioning],
  );

  const goToNext = useCallback(() => {
    goToIndex((currentIndex + 1) % items.length);
  }, [currentIndex, items.length, goToIndex]);

  const goToPrevious = useCallback(() => {
    goToIndex((currentIndex - 1 + items.length) % items.length);
  }, [currentIndex, items.length, goToIndex]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isAutoTransitioning && autoTransitionInterval > 0) {
      intervalId = setInterval(goToNext, autoTransitionInterval);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isAutoTransitioning, autoTransitionInterval, goToNext]);

  const handleMouseEnter = () => setIsAutoTransitioning(false);
  const handleMouseLeave = () => setIsAutoTransitioning(true);

  return (
    <Box sx={containerSx} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Box sx={contentSx}>
        {renderItem(items[currentIndex], !isTransitioning)}
        <Box sx={arrowsSx}>
          <IconButton id="carousel-prev-button" onClick={goToPrevious} sx={arrowButtonSx}>
            <AppIcon
              strokeWidth="thin"
              iconName="ArrowLeft"
              diameter="24px"
              color="Colors/Foreground/fg-primary"
            />
          </IconButton>
          <IconButton id="carousel-next-button" onClick={goToNext} sx={arrowButtonSx}>
            <AppIcon
              strokeWidth="thin"
              iconName="ArrowRight"
              diameter="24px"
              color="Colors/Foreground/fg-primary"
            />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

const arrowButtonSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-lg'],
  width: 'fit-content',
  borderRadius: '50%',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const arrowsSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-4xl'],
  display: 'flex',
  position: 'absolute',
  bottom: {
    md: '0%',
    lg: '3%',
    xl: '5%',
  },
  '@media (min-height: 590px)': {
    bottom: '5dvh',
  },
  '@media (min-height: 740px)': {
    bottom: '15dvh',
  },
  '@media (min-height: 975px)': {
    bottom: '20dvh',
  },
  '@media (min-height: 1145px)': {
    bottom: '25dvh',
  },
  zIndex: 3,
});

const containerSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const contentSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
};
